import { type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

// components
import { FormattedMessage, useIntl } from 'dibs-react-intl';

// styles
import styles from './PdpSharedSellerInfo.scss';

// types
import { type PdpSharedSellerInfo_seller$key as Seller } from './__generated__/PdpSharedSellerInfo_seller.graphql';

const MINIMUM_ORDER_QUANTITY = 5;

const formatResponseTime = (responseTime: number | null): ReactNode => {
    if (!responseTime || isNaN(responseTime)) {
        return null;
    }

    if (responseTime > 168) {
        return (
            <FormattedMessage id="PdpSharedSellerInfo.responseTimeWeek" defaultMessage="A week" />
        );
    }

    if (responseTime > 48) {
        return (
            <FormattedMessage
                id="PdpSharedSellerInfo.responseTimeSeveralDays"
                defaultMessage="Several days"
            />
        );
    }

    if (responseTime > 23) {
        return (
            <FormattedMessage
                id="PdpSharedSellerInfo.responseTimeFewDays"
                defaultMessage="1 to 2 days"
            />
        );
    }

    return (
        <FormattedMessage
            id="PdpSharedSellerInfo.responseTimeValue"
            defaultMessage="{responseTime, select,
              0 {<1 hour}
              1 {1 hour}
              other {{responseTime} hours}
            }"
            values={{ responseTime }}
        />
    );
};

const sellerFragment = graphql`
    fragment PdpSharedSellerInfo_seller on Seller {
        agreementStart
        sellerOrderQuantity
        analytics {
            metrics {
                responseTime {
                    value
                }
            }
        }
        tradeAssociations {
            serviceId
            displayName
        }
        liveStorefrontProfile {
            establishmentYear
        }
    }
`;

export const PdpSharedSellerInfo: FC<{ seller: Seller }> = ({ seller: sellerRef }) => {
    const { formatNumber } = useIntl();
    const seller = useFragment(sellerFragment, sellerRef);
    const {
        analytics,
        agreementStart,
        tradeAssociations,
        sellerOrderQuantity,
        liveStorefrontProfile,
    } = seller;
    const establishmentYear = liveStorefrontProfile?.establishmentYear;
    const dealerSince = agreementStart ? new Date(agreementStart).getUTCFullYear() : null;
    const responseTime = formatResponseTime(analytics?.metrics?.responseTime?.value || null);

    return (
        <>
            {establishmentYear && (
                <div className={styles.info} data-tn="dealer-establishmentYear">
                    <FormattedMessage
                        id="PdpSharedSellerInfo.establishmentYear"
                        defaultMessage="Established in {establishmentYear}"
                        values={{ establishmentYear }}
                    />
                </div>
            )}
            {dealerSince && (
                <div className={styles.info} data-tn="dealer-since">
                    <FormattedMessage
                        id="PdpSharedSellerInfo.since"
                        defaultMessage="1stDibs seller since {dealerSince}"
                        values={{ dealerSince }}
                    />
                </div>
            )}
            {sellerOrderQuantity && sellerOrderQuantity >= MINIMUM_ORDER_QUANTITY && (
                <div className={styles.info} data-tn="dealer-orderQuantity">
                    <FormattedMessage
                        id="PdpSharedSellerInfo.sellerOrderQuantity"
                        defaultMessage="{sellerOrderQuantity} sales on 1stDibs"
                        values={{ sellerOrderQuantity: formatNumber(sellerOrderQuantity) }}
                    />
                </div>
            )}
            {responseTime && (
                <div className={styles.info} data-tn="dealer-response-time">
                    <FormattedMessage
                        id="PdpSharedSellerInfo.response_time"
                        defaultMessage="Typical response time: {responseTime}"
                        values={{ responseTime }}
                    />
                </div>
            )}
            {tradeAssociations && !!tradeAssociations.length && (
                <div className={styles.associations} data-tn="dealer-associations">
                    <FormattedMessage
                        id="PdpSharedSellerInfo.associations"
                        defaultMessage="Associations"
                    />

                    <div className={styles.associationsList}>
                        {tradeAssociations.map(association => {
                            const { displayName, serviceId } = association || {};

                            if (!displayName || !serviceId) {
                                return null;
                            }

                            return (
                                <span key={serviceId} className={styles.info}>
                                    {displayName}
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}
        </>
    );
};
